import React, { useState, useEffect } from 'react';
import './App.css';
import { Home, Users, Activity, LayoutDashboard, Thermometer, BarChart2, Wrench, Shield, Zap, Calendar, Coffee, Heart, Star, Leaf, Box, Navigation, GitBranch, Map, Maximize2, Radio, Video, Volume2, ShoppingBag, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';

// Import types and functions from ResidentMonitoring
import { Resident, StaffMember, StaffingRequirement, generateMockResidents, generateMockStaffing } from './components/ResidentMonitoring';

import SpaceUtilization from './components/SpaceUtilization';
import MeetingRoomAnalytics from './components/MeetingRoomAnalytics';
import EmployeeFlow from './components/EmployeeFlow';
import WorkplaceAnalytics from './components/WorkplaceAnalytics';
import EnvironmentalMonitoring from './components/EnvironmentalMonitoring';
import FacilitiesManagement from './components/FacilitiesManagement';
import CommonAreas from './components/CommonAreas';
import Security from './components/Security';
import EnergyManagement from './components/EnergyManagement';
import PredictiveMaintenance from './components/PredictiveMaintenance';
import BuildingAutomation from './components/BuildingAutomation';
import AdvancedSecurity from './components/AdvancedSecurity';
import BehavioralAnalytics from './components/BehavioralAnalytics';
import ResourceOptimization from './components/ResourceOptimization';
import WellnessMetrics from './components/WellnessMetrics';
import EmergencyResponse from './components/EmergencyResponse';
import SpacePlanning from './components/SpacePlanning';
import AssetTracking from './components/AssetTracking';
import SustainabilityMetrics from './components/SustainabilityMetrics';
import PredictiveAnalytics from './components/PredictiveAnalytics';
import SpaceChat from './components/SpaceChat';
import Dashboard from './components/Dashboard';
import OccupantHeatmap from './components/OccupantHeatmap';
import SpaceOptimization from './components/SpaceOptimization';
import ResourceScheduler from './components/ResourceScheduler';
import EnergyEfficiencyController from './components/EnergyEfficiencyController';
import OccupancyManager from './components/OccupancyManager';
import MaintenanceActionCenter from './components/MaintenanceActionCenter';
import TrafficFlowManager from './components/TrafficFlowManager';
import SpaceUtilizationOptimizer from './components/SpaceUtilizationOptimizer';
import SensorIntegrationHub from './components/SensorIntegrationHub';
import PathAnalysisStudio from './components/PathAnalysisStudio';
import RehabilitationAnalytics from './components/RehabilitationAnalytics';
import NoiseAnalytics from './components/NoiseAnalytics';
import NeuroGaitAnalytics from './components/NeuroGaitAnalytics';
import FamilyCaregiverPortal from './components/FamilyCaregiverPortal';
import ResidentMonitoring from './components/ResidentMonitoring';

// Retail components
import RetailDashboard from './components/retail/RetailDashboard';
import StoreOccupancy from './components/retail/StoreOccupancy';
import ShopperJourney from './components/retail/ShopperJourney';
import ProductInteraction from './components/retail/ProductInteraction';
import QueueManagement from './components/retail/QueueManagement';
import StoreDesign from './components/retail/StoreDesign';
import StaffOptimization from './components/retail/StaffOptimization';
import MarketingEffectiveness from './components/retail/MarketingEffectiveness';
import RetailFacilities from './components/retail/RetailFacilities';

type Segment = 'commercial' | 'healthcare' | 'retail';

interface RoomBooking {
  startTime: string;
  endTime: string;
  attendees: number;
  bookedBy: string;
  bookingTime: Date;
}

interface NewBooking extends RoomBooking {
  roomId: string;
}

interface Ticket {
  id: string;
  type: 'CLEANING' | 'MAINTENANCE' | 'SUPPLY';
  status: 'OPEN' | 'IN_PROGRESS' | 'COMPLETED';
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
  createdAt: Date;
  updatedAt: Date;
  area?: string;
  reason?: string;
  system?: string;
  issue?: string;
  item?: string;
  level?: number;
  assignedTo?: string;
  notes?: string[];
}

const AppContent: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const [currentSegment, setCurrentSegment] = useState<Segment>(() => {
    const saved = sessionStorage.getItem('sca_demo_segment');
    return (saved as Segment) || 'commercial';
  });
  const [currentView, setCurrentView] = useState('dashboard');
  const [latestBooking, setLatestBooking] = useState<NewBooking | undefined>();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem('sca_nav_collapsed');
    return saved === 'true';
  });
  
  useEffect(() => {
    sessionStorage.setItem('sca_demo_segment', currentSegment);
  }, [currentSegment]);

  useEffect(() => {
    localStorage.setItem('sca_nav_collapsed', isSidebarCollapsed.toString());
  }, [isSidebarCollapsed]);

  const handleSegmentChange = (segment: Segment) => {
    setCurrentSegment(segment);
    setCurrentView('dashboard');
  };
  const [residents] = useState<Resident[]>(generateMockResidents(20));
  const [staffing] = useState(generateMockStaffing());
  const [tickets, setTickets] = useState<Ticket[]>([
    {
      id: 'TKT-001',
      type: 'CLEANING',
      status: 'COMPLETED',
      priority: 'HIGH',
      createdAt: new Date(Date.now() - 86400000 * 2), // 2 days ago
      updatedAt: new Date(Date.now() - 3600000), // 1 hour ago
      area: 'Conference Room A',
      reason: 'Post-event cleanup required',
      notes: ['Deep cleaning completed', 'Carpet shampooed', 'Windows cleaned']
    },
    {
      id: 'TKT-002',
      type: 'MAINTENANCE',
      status: 'IN_PROGRESS',
      priority: 'HIGH',
      createdAt: new Date(Date.now() - 86400000), // 1 day ago
      updatedAt: new Date(Date.now() - 1800000), // 30 mins ago
      system: 'HVAC',
      issue: 'Temperature fluctuation in North Wing',
      assignedTo: 'John Smith',
      notes: ['Technician dispatched', 'Parts ordered']
    },
    {
      id: 'TKT-003',
      type: 'SUPPLY',
      status: 'OPEN',
      priority: 'LOW',
      createdAt: new Date(Date.now() - 3600000), // 1 hour ago
      updatedAt: new Date(Date.now() - 3600000),
      item: 'Printer Paper',
      level: 15,
      notes: ['Current stock below threshold']
    },
    {
      id: 'TKT-004',
      type: 'CLEANING',
      status: 'IN_PROGRESS',
      priority: 'MEDIUM',
      createdAt: new Date(Date.now() - 43200000), // 12 hours ago
      updatedAt: new Date(Date.now() - 1800000), // 30 mins ago
      area: 'Break Room',
      reason: 'Coffee machine area needs attention',
      assignedTo: 'Maria Garcia',
      notes: ['Cleaning in progress']
    },
    {
      id: 'TKT-005',
      type: 'MAINTENANCE',
      status: 'COMPLETED',
      priority: 'MEDIUM',
      createdAt: new Date(Date.now() - 172800000), // 2 days ago
      updatedAt: new Date(Date.now() - 7200000), // 2 hours ago
      system: 'Lighting',
      issue: 'Flickering lights in South Wing corridor',
      assignedTo: 'Bob Wilson',
      notes: ['Ballast replaced', 'All lights tested and working']
    },
    {
      id: 'TKT-006',
      type: 'SUPPLY',
      status: 'IN_PROGRESS',
      priority: 'MEDIUM',
      createdAt: new Date(Date.now() - 21600000), // 6 hours ago
      updatedAt: new Date(Date.now() - 900000), // 15 mins ago
      item: 'Hand Soap',
      level: 25,
      assignedTo: 'Supply Team',
      notes: ['Order placed', 'Delivery expected tomorrow']
    }
  ]);

  console.log("Current view:", currentView);

  // Define navigation packs for each segment
  const commercialNavItems = [
    { name: 'Home', view: 'dashboard', icon: Home },
    { name: 'Space Utilization', view: 'space utilization', icon: LayoutDashboard },
    { name: 'Path Studio', view: 'path analysis', icon: GitBranch },
    { name: 'Space Optimization', view: 'space optimization', icon: Maximize2 },
    { name: 'Facilities Care', view: 'facilities', icon: Wrench },
    { name: 'Energy', view: 'energy', icon: Zap },
    { name: 'Security', view: 'security', icon: Shield }
  ];

  const healthcareNavItems = [
    { name: 'Dashboard', view: 'dashboard', icon: Home },
    { name: 'Resident Monitoring', view: 'resident-monitoring', icon: Users },
    { name: 'Memory Care', view: 'memory-care', icon: Activity },
    { name: 'Rehabilitation', view: 'rehabilitation', icon: Activity },
    { name: 'Family Portal', view: 'family-portal', icon: Heart },
    { name: 'Noise & Wellness', view: 'noise', icon: Volume2 },
    { name: 'Space & Common Areas', view: 'space utilization', icon: LayoutDashboard },
    { name: 'Path Analysis', view: 'path analysis', icon: Video },
    { name: 'Facilities', view: 'facilities', icon: Wrench },
    { name: 'Safety', view: 'security', icon: Shield }
  ];

  const retailNavItems = [
    { name: 'Dashboard', view: 'retail-dashboard', icon: Home },
    { name: 'Store Occupancy', view: 'retail-occupancy', icon: Users },
    { name: 'Shopper Journey', view: 'shopper-journey', icon: Activity },
    { name: 'Product Interaction', view: 'product-interaction', icon: ShoppingBag },
    { name: 'Queue Management', view: 'queue-management', icon: Users },
    { name: 'Store Design', view: 'store-design', icon: LayoutDashboard },
    { name: 'Staff Optimization', view: 'staff-optimization', icon: Users },
    { name: 'Marketing Effectiveness', view: 'marketing-effectiveness', icon: TrendingUp },
    { name: 'Path Analysis', view: 'path analysis', icon: Video },
    { name: 'Facilities', view: 'retail-facilities', icon: Wrench }
  ];

  const getNavItems = () => {
    switch (currentSegment) {
      case 'commercial': return commercialNavItems;
      case 'healthcare': return healthcareNavItems;
      case 'retail': return retailNavItems;
      default: return commercialNavItems;
    }
  };

  const navItems = getNavItems();

  const getShiftStatus = (requirement: StaffingRequirement) => {
    const nurseStatus = requirement.currentNurses >= requirement.requiredNurses;
    const caregiverStatus = requirement.currentCaregivers >= requirement.requiredCaregivers;
    if (nurseStatus && caregiverStatus) return 'optimal';
    if (requirement.currentNurses === 0 || requirement.currentCaregivers === 0) return 'critical';
    return 'needs_staff';
  };

  // Handle room bookings
  const handleRoomBooking = (roomId: string, timeSlot: string) => {
    const [startTime, endTime] = timeSlot.split('-');
    const booking: NewBooking = {
      roomId,
      startTime,
      endTime,
      attendees: 1, // Default value, could be passed from chat in the future
      bookedBy: 'Current User',
      bookingTime: new Date()
    };
    
    console.log(`Booking room ${roomId} for ${timeSlot}`);
    setLatestBooking(booking);
    
    // In a real app, this would update a backend service
    console.log('Booking details:', booking);
  };

  // Handle ticket creation
  const handleCreateCleaningTicket = (area: string, reason: string) => {
    const newTicket: Ticket = {
      id: Date.now().toString(),
      type: 'CLEANING',
      status: 'OPEN',
      priority: 'MEDIUM',
      createdAt: new Date(),
      updatedAt: new Date(),
      area,
      reason
    };
    setTickets(prev => [...prev, newTicket]);
    console.log('Created cleaning ticket:', newTicket);
  };

  const handleCreateMaintenanceTicket = (system: string, issue: string) => {
    const newTicket: Ticket = {
      id: Date.now().toString(),
      type: 'MAINTENANCE',
      status: 'OPEN',
      priority: 'MEDIUM',
      createdAt: new Date(),
      updatedAt: new Date(),
      system,
      issue
    };
    setTickets(prev => [...prev, newTicket]);
    console.log('Created maintenance ticket:', newTicket);
  };

  const handleCreateSupplyTicket = (item: string, level: number) => {
    const newTicket: Ticket = {
      id: Date.now().toString(),
      type: 'SUPPLY',
      status: 'OPEN',
      priority: 'LOW',
      createdAt: new Date(),
      updatedAt: new Date(),
      item,
      level
    };
    setTickets(prev => [...prev, newTicket]);
    console.log('Created supply ticket:', newTicket);
  };

  // Consolidated building data for the chat interface
  const buildingData = {
    cleaning: {
      todayTasks: [
        {
          id: 'CT-001',
          area: 'Conference Room A',
          utilization: 85,
          utilizationThreshold: 75
        },
        {
          id: 'CT-002',
          area: 'Open Office North',
          utilization: 72,
          utilizationThreshold: 75
        },
        {
          id: 'CT-003',
          area: 'Break Room',
          utilization: 92,
          utilizationThreshold: 80
        },
        {
          id: 'CT-004',
          area: 'Executive Wing',
          utilization: 88,
          utilizationThreshold: 70
        }
      ]
    },
    occupancy: {
      currentTotal: 342,
      capacity: 500,
      peakHours: '10:00 AM - 2:00 PM',
      byZone: [
        {
          zone: 'North Wing',
          count: 120,
          capacity: 150
        },
        {
          zone: 'South Wing',
          count: 85,
          capacity: 125
        },
        {
          zone: 'East Wing',
          count: 78,
          capacity: 100
        },
        {
          zone: 'West Wing',
          count: 59,
          capacity: 125
        }
      ],
      behavioralInsights: [
        { type: 'Collaboration', value: '35%', trend: 'Increasing', impact: 'high' as const },
        { type: 'Focus Work', value: '45%', trend: 'Stable', impact: 'medium' as const },
        { type: 'Social Interaction', value: '20%', trend: 'Decreasing', impact: 'low' as const }
      ]
    },
    spaceEfficiency: {
      overall: 78,
      categories: {
        pathEfficiency: 82,
        spaceUtilization: 75,
        collaboration: 85,
        roomFit: 70
      },
      recommendations: [
        {
          category: 'Path Efficiency',
          score: 82,
          impact: 'medium' as const,
          suggestion: 'Optimize printer placement to reduce travel distance'
        },
        {
          category: 'Space Utilization',
          score: 75,
          impact: 'high' as const,
          suggestion: 'Rightsize meeting rooms based on actual usage patterns'
        }
      ]
    },
    pathAnalytics: {
      averagePathLength: '250 ft',
      averageDuration: '28 mins',
      commonPatterns: [
        'Desk → Meeting Room → Break Area',
        'Entry → Desk → Collaboration Zone',
        'Meeting Room → Phone Booth → Desk'
      ],
      peakInteractionAreas: [
        { area: 'Central Hub', interactions: 145 },
        { area: 'Coffee Station', interactions: 98 },
        { area: 'Print Station', interactions: 67 }
      ]
    },
    badgeAnalytics: {
      today: {
        totalScans: 487,
        activeUsers: 342,
        shortVisits: 89,
        avgDuration: '6.5 hours',
        peakEntry: '9:00 AM',
        unusualPatterns: [
          { time: '10:30 AM', event: 'Multiple rapid scans', count: 5 },
          { time: '2:15 PM', event: 'Badge used but no path detected', count: 3 }
        ]
      }
    },
    rooms: {
      all: [
        {
          id: 'CONF_A',
          name: 'Conference Room A',
          status: 'available' as const,
          sensorData: {
            activePaths: 0,
            lastActivity: null,
            averageOccupancy: 0.75
          }
        },
        {
          id: 'CONF_B',
          name: 'Conference Room B',
          status: 'occupied' as const,
          sensorData: {
            activePaths: 4,
            lastActivity: new Date(),
            averageOccupancy: 0.85
          }
        },
        {
          id: 'FOCUS_1',
          name: 'Focus Room 1',
          status: 'available' as const,
          sensorData: {
            activePaths: 0,
            lastActivity: null,
            averageOccupancy: 0.60
          }
        },
        {
          id: 'FOCUS_2',
          name: 'Focus Room 2',
          status: 'booked' as const,
          sensorData: {
            activePaths: 2,
            lastActivity: new Date(),
            averageOccupancy: 0.70
          }
        },
        {
          id: 'REHAB_1',
          name: 'Rehabilitation Room 1',
          status: 'occupied' as const,
          sensorData: {
            activePaths: 3,
            lastActivity: new Date(),
            averageOccupancy: 0.90
          }
        },
        {
          id: 'REHAB_2',
          name: 'Rehabilitation Room 2',
          status: 'available' as const,
          sensorData: {
            activePaths: 0,
            lastActivity: null,
            averageOccupancy: 0.80
          }
        }
      ]
    },
    environment: {
      temperature: 72,
      humidity: 45,
      co2: 650,
      noise: 45
    },
    flooringRecommendations: {
      zones: [
        {
          zone: 'Main Corridor',
          footTraffic: 2450,
          risk: 'High',
          recommendedFlooring: {
            product: 'Commercial Grade Vinyl Sheet Flooring',
            features: ['Superior durability', 'Infection control', 'Slip resistant', 'Low maintenance']
          }
        },
        {
          zone: 'Open Office North',
          footTraffic: 1850,
          risk: 'Moderate',
          recommendedFlooring: {
            product: 'Premium Carpet Tiles',
            features: ['Acoustic performance', 'Ergonomic comfort', 'Stain resistant', 'Modular design']
          }
        },
        {
          zone: 'Meeting Areas',
          footTraffic: 1200,
          risk: 'Low',
          recommendedFlooring: {
            product: 'Luxury Vinyl Tiles',
            features: ['No-polish option', 'Superior acoustics', 'Easy installation', 'Easy maintenance']
          }
        },
        {
          zone: 'Break Room',
          footTraffic: 950,
          risk: 'Moderate',
          recommendedFlooring: {
            product: 'Safety Sheet Vinyl',
            features: ['Enhanced slip resistance', 'Stain protection', 'Impact resistant', 'Moisture resistant']
          }
        }
      ],
      generalInfo: {
        supplier: 'Commercial Flooring Provider',
        website: 'flooringprovider.com',
        specialties: ['Healthcare', 'Senior Living', 'Education', 'Workplace'],
        certifications: ['Advanced Wear Layer Technology', 'Environmental Product Declarations']
      }
    },
    healthcare: {
      residents: {
        total: residents.length,
        byStatus: {
          stable: residents.filter(r => r.status === 'stable').length,
          needsAttention: residents.filter(r => r.status === 'needs_attention').length,
          urgent: residents.filter(r => r.status === 'urgent').length
        },
        details: residents.map(resident => ({
          id: resident.id,
          name: resident.name,
          room: resident.roomNumber,
          status: resident.status,
          vitals: resident.vitals,
          metrics: resident.metrics,
          alerts: resident.alerts,
          lastActivity: resident.lastActivity
        }))
      },
      staffing: {
        shifts: staffing.requirements.map((req: StaffingRequirement) => ({
          shift: req.shift,
          status: getShiftStatus(req),
          staffing: {
            nurses: {
              required: req.requiredNurses,
              current: req.currentNurses
            },
            caregivers: {
              required: req.requiredCaregivers,
              current: req.currentCaregivers
            }
          },
          staff: staffing.currentStaff
            .filter((s: StaffMember) => s.shift === req.shift)
            .map((s: StaffMember) => ({
              id: s.id,
              name: s.name,
              role: s.role,
              assignedResidents: s.assignedResidents
            }))
        })),
        coverage: {
          morning: {
            start: '7:00 AM',
            end: '3:00 PM',
            handover: '2:30 PM - 3:00 PM'
          },
          afternoon: {
            start: '3:00 PM',
            end: '11:00 PM',
            handover: '10:30 PM - 11:00 PM'
          },
          night: {
            start: '11:00 PM',
            end: '7:00 AM',
            handover: '6:30 AM - 7:00 AM'
          }
        },
        specializedCare: {
          memorySupport: ['Morning Exercise Program', 'Cognitive Stimulation Therapy'],
          rehabilitation: ['Physical Therapy', 'Occupational Therapy'],
          nutrition: ['Meal Assistance', 'Dietary Monitoring'],
          wellness: ['Social Activities', 'Mental Health Support']
        }
      },
      quickResponses: {
        staffingQueries: [
          'Who is working the morning shift?',
          'When is the next shift handover?',
          'How many nurses are on duty?',
          'Which residents need immediate attention?',
          'Show me understaffed shifts'
        ],
        residentQueries: [
          'Show residents with urgent status',
          'List all active alerts',
          'Which residents have missed medications?',
          'Show recent vital sign changes',
          'List residents in memory care'
        ],
        careQueries: [
          'Schedule a care team meeting',
          'Update resident care plan',
          'Record medication administration',
          'Log resident activity',
          'Request specialist consultation'
        ]
      }
    }
  };

  const renderMainContent = () => {
    console.log("Rendering main content, currentView:", currentView);
    switch (currentView) {
      case 'dashboard':
        return <Dashboard buildingData={buildingData} onNavigate={setCurrentView} segment={currentSegment} />;
      case 'retail-dashboard':
        return <RetailDashboard onNavigate={setCurrentView} />;
      case 'retail-occupancy':
        return <StoreOccupancy />;
      case 'shopper-journey':
        return <ShopperJourney />;
      case 'product-interaction':
        return <ProductInteraction />;
      case 'queue-management':
        return <QueueManagement />;
      case 'store-design':
        return <StoreDesign />;
      case 'staff-optimization':
        return <StaffOptimization />;
      case 'marketing-effectiveness':
        return <MarketingEffectiveness />;
      case 'retail-facilities':
        return <RetailFacilities />;
      case 'meeting rooms':
        return <MeetingRoomAnalytics newBooking={latestBooking} />;
      case 'space utilization':
        return <SpaceUtilization />;
      case 'predictive maintenance':
        return <PredictiveMaintenance />;
      case 'building automation':
        return <BuildingAutomation />;
      case 'advanced security':
        return <AdvancedSecurity />;
      case 'behavioral analytics':
        return <BehavioralAnalytics />;
      case 'resource optimization':
        return <ResourceOptimization />;
      case 'wellness metrics':
        return <WellnessMetrics />;
      case 'facilities':
        return <FacilitiesManagement tickets={tickets} />;
      case 'employee flow':
        return <EmployeeFlow />;
      case 'common areas':
        return <CommonAreas />;
      case 'environmental':
        return <EnvironmentalMonitoring />;
      case 'security':
        return currentSegment === 'commercial' ? <AdvancedSecurity /> : <Security />;
      case 'energy':
        return <EnergyManagement />;
      case 'workplace analytics':
        return <WorkplaceAnalytics />;
      case 'emergency response':
        return <EmergencyResponse />;
      case 'space planning':
        return <SpacePlanning />;
      case 'asset tracking':
        return <AssetTracking />;
      case 'sustainability':
        return <SustainabilityMetrics />;
      case 'predictive analytics':
        return <PredictiveAnalytics />;
      case 'occupant heatmap':
        return <OccupantHeatmap />;
      case 'space optimization':
        return <SpaceOptimization />;
      case 'resource scheduler':
        return <ResourceScheduler />;
      case 'energy controller':
        return <EnergyEfficiencyController />;
      case 'occupancy manager':
        return <OccupancyManager />;
      case 'maintenance':
        return <MaintenanceActionCenter />;
      case 'traffic flow':
        return <TrafficFlowManager />;
      case 'space optimizer':
        return <SpaceUtilizationOptimizer />;
      case 'sensor hub':
        return <SensorIntegrationHub />;
      case 'path analysis':
        return <PathAnalysisStudio />;
      case 'rehabilitation':
        return <RehabilitationAnalytics />;
      case 'noise':
        return <NoiseAnalytics buildingData={buildingData} />;
      case 'memory-care':
        return <NeuroGaitAnalytics />;
      case 'family-portal':
        return <FamilyCaregiverPortal />;
      case 'resident-monitoring':
        return <ResidentMonitoring />;
      default:
        return <h2>Select a view</h2>;
    }
  };

  if (!currentUser) {
    return <Login />;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--sca-cream)' }}>
      <header className="app-header">
        <div className="flex items-center justify-between w-full px-6 py-3">
          <div className="flex items-center gap-4">
            <img src="/scanalytics-logo-square.png" alt="Scanalytics Logo" className="h-10" />
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium" style={{ color: 'var(--sca-ink)' }}>
                Scanalytics Demo
              </span>
              <span className="text-xs" style={{ color: 'var(--sca-muted)' }}>
                {currentSegment === 'commercial' && '| Commercial Real Estate'}
                {currentSegment === 'healthcare' && '| Healthcare / Senior Living'}
                {currentSegment === 'retail' && '| Retail'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--sca-hover)', border: '1px solid var(--sca-border)' }}>
              <button
                onClick={() => handleSegmentChange('commercial')}
                className="px-4 py-1.5 text-sm rounded-md transition-all"
                style={{
                  backgroundColor: currentSegment === 'commercial' ? 'var(--sca-ink)' : 'transparent',
                  color: currentSegment === 'commercial' ? 'var(--sca-card)' : 'var(--sca-ink)',
                  border: 'none',
                  fontWeight: currentSegment === 'commercial' ? '500' : '400'
                }}
              >
                Commercial Real Estate
              </button>
              <button
                onClick={() => handleSegmentChange('healthcare')}
                className="px-4 py-1.5 text-sm rounded-md transition-all"
                style={{
                  backgroundColor: currentSegment === 'healthcare' ? 'var(--sca-ink)' : 'transparent',
                  color: currentSegment === 'healthcare' ? 'var(--sca-card)' : 'var(--sca-ink)',
                  border: 'none',
                  fontWeight: currentSegment === 'healthcare' ? '500' : '400'
                }}
              >
                Healthcare / Senior Living
              </button>
              <button
                onClick={() => handleSegmentChange('retail')}
                className="px-4 py-1.5 text-sm rounded-md transition-all"
                style={{
                  backgroundColor: currentSegment === 'retail' ? 'var(--sca-ink)' : 'transparent',
                  color: currentSegment === 'retail' ? 'var(--sca-card)' : 'var(--sca-ink)',
                  border: 'none',
                  fontWeight: currentSegment === 'retail' ? '500' : '400'
                }}
              >
                Retail
              </button>
            </div>
            <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>{currentUser.email}</span>
            <button
              onClick={() => logout()}
              className="px-4 py-1.5 text-sm rounded-full transition-colors"
              style={{ color: 'var(--sca-muted)', border: '1px solid var(--sca-border)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--sca-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>
      <div className="app-content">
        <nav className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="collapse-toggle"
              title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isSidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </button>
          </div>
          {navItems.map((item) => (
            <button
              key={item.view}
              onClick={() => setCurrentView(item.view)}
              className={`nav-item ${currentView === item.view ? 'active' : ''}`}
              title={isSidebarCollapsed ? item.name : ''}
            >
              <item.icon size={18} />
              {!isSidebarCollapsed && <span>{item.name}</span>}
            </button>
          ))}
        </nav>
        <main className="flex-1">
          {renderMainContent()}
        </main>
      </div>

      {/* Chat interface */}
      <SpaceChat
        buildingData={buildingData}
        onBookRoom={handleRoomBooking}
        onReserveDesk={(deskId, timeSlot) => console.log(`Reserving desk ${deskId} for ${timeSlot}`)}
        onCreateCleaningTicket={handleCreateCleaningTicket}
        onCreateMaintenanceTicket={handleCreateMaintenanceTicket}
        onCreateSupplyTicket={handleCreateSupplyTicket}
      />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;